import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ExternalLink, Loader2, Search, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface LeadOption {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

interface TaskLeadSelectProps {
  workspaceId: string;
  value: string | null;
  onChange: (leadId: string | null) => void;
  /** Nome do lead já vinculado (ex.: ao editar) */
  linkedLeadName?: string | null;
}

export function TaskLeadSelect({
  workspaceId,
  value,
  onChange,
  linkedLeadName,
}: TaskLeadSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<LeadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchLeads = useCallback(
    async (term: string) => {
      if (!workspaceId || term.length < 2) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const { data, error } = await supabase
          .from("leads")
          .select("id, name, phone, email")
          .eq("workspace_id", workspaceId)
          .or(`name.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`)
          .order("name", { ascending: true })
          .limit(20);
        if (error) throw error;
        setResults((data ?? []) as LeadOption[]);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    if (!value) {
      setSelectedLead(null);
      return;
    }
    if (selectedLead?.id === value) return;
    void (async () => {
      const { data } = await supabase
        .from("leads")
        .select("id, name, phone, email")
        .eq("id", value)
        .maybeSingle();
      if (data) {
        setSelectedLead(data as LeadOption);
      } else if (linkedLeadName) {
        setSelectedLead({ id: value, name: linkedLeadName, phone: null, email: null });
      }
    })();
  }, [value, linkedLeadName, selectedLead?.id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLeads(searchTerm.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, searchLeads]);

  const displayLead = selectedLead ?? (value && linkedLeadName ? { id: value, name: linkedLeadName, phone: null, email: null } : null);

  return (
    <div className="space-y-2">
      <Label>Lead (opcional)</Label>
      {displayLead ? (
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm min-w-0">
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium truncate flex-1">{displayLead.name}</span>
          {displayLead.phone && (
            <span className="text-muted-foreground text-xs shrink-0 hidden sm:inline">
              {displayLead.phone}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => {
              onChange(null);
              setSelectedLead(null);
            }}
            aria-label="Remover lead"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              className="w-full justify-start text-left font-normal text-muted-foreground"
            >
              <Search className="mr-2 h-4 w-4 shrink-0" />
              Buscar lead...
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Nome, telefone ou e-mail..."
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
              <CommandList>
                {searching && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!searching && searchTerm.trim().length >= 2 && results.length === 0 && (
                  <CommandEmpty>Nenhum lead encontrado</CommandEmpty>
                )}
                {!searching && searchTerm.trim().length < 2 && searchTerm.length > 0 && (
                  <div className="py-3 text-center text-xs text-muted-foreground">
                    Digite pelo menos 2 caracteres
                  </div>
                )}
                <CommandGroup>
                  {results.map((lead) => (
                    <CommandItem
                      key={lead.id}
                      value={lead.id}
                      onSelect={() => {
                        setSelectedLead(lead);
                        onChange(lead.id);
                        setOpen(false);
                        setSearchTerm("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === lead.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{lead.name}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          {[lead.phone, lead.email].filter(Boolean).join(" · ") || "Sem contato"}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      {displayLead && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          asChild
        >
          <a href={`/conversations?lead_id=${displayLead.id}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3 w-3 mr-1" />
            Abrir lead
          </a>
        </Button>
      )}
    </div>
  );
}
